import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { IonContent, IonPage } from "@ionic/react";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import CommunityPage from "@/pages/CommunityPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import HelpPage from "@/pages/HelpPage";
import SectionPage, { SECTIONS, LEGACY_REDIRECTS, LegacyRedirect } from "@/pages/SectionPage";
import DashboardLayout from "@/components/DashboardLayout";
import SessionTimeout from "@/components/SessionTimeout";
import { ConfirmationProvider } from "@/components/ConfirmationProvider";

// The API is served by the same Express server as this app, so by default call it on the
// current origin. Set REACT_APP_BACKEND_URL at build time only to point at a separate API host.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("thrively_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback at /auth/google will exchange the code and establish the session first.
    if (window.location.pathname === '/auth/google' && window.location.search.includes('code=')) {
      setLoading(false);
      return;
    }
    const initAuth = async () => {
      const savedToken = localStorage.getItem("thrively_token");
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem("thrively_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token } = response.data;
    localStorage.setItem("thrively_token", access_token);
    setToken(access_token);
    // Fetch full user payload (includes subscription_tier, limits, usage)
    const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${access_token}` } });
    setUser(meRes.data);
    return meRes.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token } = response.data;
    localStorage.setItem("thrively_token", access_token);
    setToken(access_token);
    const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${access_token}` } });
    setUser(meRes.data);
    return meRes.data;
  };

  const refreshUser = async () => {
    if (!token) return null;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
      return res.data;
    } catch { return null; }
  };

  const logout = () => {
    localStorage.removeItem("thrively_token");
    setToken(null);
    setUser(null);
  };

  const setAuthData = (newToken, userData) => {
    localStorage.setItem("thrively_token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    setAuthData,
    refreshUser,
    isAuthenticated: !!token && !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center">
          <img 
            src="/thrively-logo.png" 
            alt="Thrively" 
            className="h-16 w-16 mb-4"
          />
          <span className="text-primary font-heading font-bold">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

const PublicPage = ({ children }) => (
  <IonPage className="public-page">
    <IonContent className="public-scroll-content">
      {children}
    </IonContent>
  </IonPage>
);

// API Helper with auth
export const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("thrively_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only auto-logout for auth-related 401s, not integration endpoints
      const url = error.config?.url || '';
      const isIntegrationEndpoint = url.includes('/drive/') || url.includes('/dropbox/') || url.includes('/oauth/');
      if (!isIntegrationEndpoint) {
        localStorage.removeItem("thrively_token");
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

// App Router that handles OAuth callback
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<PublicPage><LandingPage /></PublicPage>} />
      <Route path="/auth/google" element={<PublicPage><AuthCallback /></PublicPage>} />
      <Route path="/privacy-policy" element={<PublicPage><PrivacyPolicyPage /></PublicPage>} />
      <Route path="/forgot-password" element={<PublicPage><ForgotPasswordPage /></PublicPage>} />
      <Route path="/reset-password" element={<PublicPage><ResetPasswordPage /></PublicPage>} />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <HelpPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Sectioned app: /<section> redirects to its first tab, /<section>/<tab> renders it */}
      {SECTIONS.flatMap((section) => [
        <Route
          key={section.key}
          path={section.path}
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <SectionPage sectionKey={section.key} />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />,
        <Route
          key={`${section.key}-tab`}
          path={`${section.path}/:tab`}
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <SectionPage sectionKey={section.key} />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
      ])}
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CommunityPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Old bookmarks / links keep working */}
      {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
      ))}
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ConfirmationProvider>
          <Toaster position="top-right" richColors />
          <SessionTimeout />
          <AppRouter />
        </ConfirmationProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
